import { Sparkle } from "./Sparkles";
import { img } from "@/lib/images";

const reduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Layered hero image composition (portrait photos):
 * - large rounded image (aspect 4/5) with a soft pink glow behind it
 * - a second smaller image (aspect 3/4) overlapping the bottom-left corner
 * - a floating "Residential & Commercial" pill with a gold sparkle
 * Gently parallaxes (moves up slower than the page) on scroll.
 */
export function HeroImageGroup({
  primary,
  primaryAlt,
  secondary,
  secondaryAlt,
}: {
  primary: string;
  primaryAlt: string;
  secondary: string;
  secondaryAlt: string;
}) {
  useParallax();

  return (
    <div data-hero-group className="relative">
      {/* pink glow */}
      <div
        className="pointer-events-none absolute -inset-6 -z-10 rounded-[2.5rem] bg-primary/20 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-10 top-1/3 -z-10 h-40 w-40 rounded-full bg-gold/25 blur-2xl"
        aria-hidden
      />

      {/* primary */}
      <div className="overflow-hidden rounded-3xl border border-white/60 bg-secondary shadow-[0_40px_80px_-40px_rgba(26,26,26,0.55)]">
        <img
          src={img(primary, 560, 70)}
          srcSet={`${img(primary, 400, 70)} 400w, ${img(primary, 560, 70)} 560w, ${img(primary, 680, 70)} 680w, ${img(primary, 800, 70)} 800w, ${img(primary, 1200, 70)} 1200w`}
          sizes="(min-width: 1024px) 530px, calc(100vw - 32px)"
          alt={primaryAlt}
          width={1200}
          height={1600}
          className="aspect-[4/5] w-full object-cover"
          fetchPriority="high"
        />
      </div>

      {/* secondary overlapping bottom-left */}
      <div className="absolute -bottom-8 -left-4 hidden w-32 overflow-hidden rounded-2xl border-4 border-background bg-secondary shadow-xl md:w-40 lg:block">
        <img
          src={img(secondary, 320, 80)}
          srcSet={`${img(secondary, 240, 80)} 240w, ${img(secondary, 320, 80)} 320w, ${img(secondary, 480, 80)} 480w`}
          sizes="(min-width: 1024px) 160px, 40vw"
          alt={secondaryAlt}
          width={480}
          height={640}
          loading="lazy"
          decoding="async"
          className="aspect-[3/4] w-full object-cover"
        />
      </div>

      {/* floating pill */}
      <div
        className="absolute -right-3 top-8 flex items-center gap-2 rounded-full border border-white/70 bg-background/90 px-4 py-2 text-xs font-semibold text-ink shadow-lg backdrop-blur md:-right-6"
        style={{ animation: reduced() ? undefined : "gentle-float 5s ease-in-out infinite" }}
      >
        <Sparkle className="h-4 w-4" color="gold" />
        Residential &amp; Commercial
      </div>
    </div>
  );
}

function useParallax() {
  // Lightweight scroll parallax on the group — up slower than the page.
  if (typeof window === "undefined") return;
  // Attach on mount via a side-effect in render is not ideal; use a microtask.
  queueMicrotask(() => {
    if (reduced()) return;
    const el = document.querySelector<HTMLElement>("[data-hero-group]");
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < window.innerHeight) {
          el.style.transform = `translateY(${y * -0.06}px)`;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
  });
}
